import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from '@/components/ui';
import QPTaskModal from '../../../components/QPTaskModal';
import RoutineTaskModal from '../../../components/RoutineTaskModal';
import QuantifiedTaskModal from '../../quantified-tracking/components/taskModals/TaskModal';
import TaskTypeSelector from '../../projects/components/TaskTypeSelector';
import { useTaskModalForemanAreas } from '../../../hooks/useTaskModalForemanAreas';
import { usePermissions } from '../../../hooks/usePermissions';
import { routineTaskService } from '../../../services/routineTaskService';
import { getFieldLockingState } from '../../../utils/fieldLocking';
import { TaskStage, type Task, type TaskType } from '../../../types/Task';
import type { RoutineTask } from '../../../types/RoutineTask';
import type { Project } from '../../../types/Project';
import type { TaskOption } from '../types';
import { loadTaskModalTarget, taskOptionFromSaved } from '../utils/taskModalDispatch';

/**
 * What the grid asked for: create a task for one row, or open an existing one.
 * The page owns this value; this component owns everything downstream of it.
 */
export type HiredEquipmentTaskModalRequest =
  | { kind: 'create'; rowClientId: string }
  | { kind: 'view'; taskId: number };

export interface HiredEquipmentTaskModalsProps {
  request: HiredEquipmentTaskModalRequest | null;
  onClose: () => void;
  /** A task was created for the requesting row. */
  onTaskCreated: (rowClientId: string, task: TaskOption) => void;
  /** An existing task was edited (name/number/status may have changed). */
  onTaskUpdated: (task: TaskOption) => void;
  /** Task types the signed-in user may create. Empty disables creation. */
  availableTypes: TaskType[];
}

type Stage = 'pick-type' | 'loading' | 'routine' | 'routine-locked' | 'quantified' | 'generic';

/** New tasks default to active: the hired-equipment task picker searches active work. */
const CREATE_DEFAULT_STATUS = 'ACTIVE';

/** A routine task in one of these states locks the edit modal, Save included. */
function isRoutineLocked(routineTask: RoutineTask | undefined): boolean {
  if (!routineTask) return false;
  return getFieldLockingState({ taskStatus: routineTask.status as TaskStage }).isLocked;
}

const HiredEquipmentTaskModals: React.FC<HiredEquipmentTaskModalsProps> = ({
  request,
  onClose,
  onTaskCreated,
  onTaskUpdated,
  availableTypes,
}) => {
  const [stage, setStage] = useState<Stage | null>(null);
  const [routineTask, setRoutineTask] = useState<RoutineTask | undefined>(undefined);
  const [task, setTask] = useState<Task | undefined>(undefined);
  const [project, setProject] = useState<Project | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reopenReason, setReopenReason] = useState('');
  const [reopening, setReopening] = useState(false);
  /** Set when a create flow picked Quantified before foreman areas finished loading. */
  const [pendingQuantifiedCreate, setPendingQuantifiedCreate] = useState(false);

  const isCreate = request?.kind === 'create';
  const { foremanAreas, error: foremanAreasError } = useTaskModalForemanAreas();
  const { hasPermission } = usePermissions();
  const canReopen = hasPermission('routine:specific_edit');

  const reset = useCallback(() => {
    setStage(null);
    setRoutineTask(undefined);
    setTask(undefined);
    setProject(null);
    setError(null);
    setReopenReason('');
    setReopening(false);
    setPendingQuantifiedCreate(false);
  }, []);

  useEffect(() => {
    if (!request) {
      reset();
      return;
    }

    if (request.kind === 'create') {
      setRoutineTask(undefined);
      setTask(undefined);
      setProject(null);
      setError(null);
      if (availableTypes.length === 1) {
        // Only one type available - skip the chooser.
        if (availableTypes[0] === 'Quantified') {
          setPendingQuantifiedCreate(true);
          setStage('loading');
        } else {
          setStage('routine');
        }
      } else {
        setStage('pick-type');
      }
      return;
    }

    let cancelled = false;
    setStage('loading');
    setError(null);
    setReopenReason('');

    loadTaskModalTarget(request.taskId)
      .then((target) => {
        if (cancelled) return;
        setProject(target.project);
        if (target.kind === 'routine') {
          setRoutineTask(target.routineTask);
          // A closed/cancelled routine task opens the edit modal with Save hidden, so
          // offer the way back to Active before showing a form nothing can be saved from.
          setStage(isRoutineLocked(target.routineTask) ? 'routine-locked' : 'routine');
        } else {
          setTask(target.task);
          setStage(target.kind === 'quantified' ? 'quantified' : 'generic');
        }
      })
      .catch((err) => {
        console.error('Failed to load task for hired equipment task modal', err);
        if (cancelled) return;
        setError('Could not load this task. Please try again.');
        setStage(null);
      });

    return () => {
      cancelled = true;
    };
  }, [request, availableTypes, reset]);

  // The quantified modal cannot save without a foreman area, so hold until they load.
  useEffect(() => {
    if (!pendingQuantifiedCreate) return;
    if (foremanAreas.length === 0 && !foremanAreasError) return;
    setPendingQuantifiedCreate(false);
    if (foremanAreasError) {
      setError('Could not load foreman areas, which are required to create a quantified task.');
      setStage(null);
      return;
    }
    setStage('quantified');
  }, [pendingQuantifiedCreate, foremanAreas, foremanAreasError]);

  const handleSelectType = (type: TaskType) => {
    if (type === 'Routine') {
      setStage('routine');
      return;
    }
    if (foremanAreas.length === 0) {
      setPendingQuantifiedCreate(true);
      setStage('loading');
      return;
    }
    setStage('quantified');
  };

  const finish = (saved: Task | RoutineTask) => {
    const option = taskOptionFromSaved(saved);
    if (request?.kind === 'create') {
      onTaskCreated(request.rowClientId, option);
    } else {
      onTaskUpdated(option);
    }
    onClose();
  };

  const handleReopen = async () => {
    if (!routineTask || !reopenReason.trim()) return;
    setReopening(true);
    setError(null);
    try {
      await routineTaskService.updateTaskStatus(routineTask.id, 'ACTIVE', reopenReason.trim());
      const refreshed = await routineTaskService.getTaskDetails(routineTask.id);
      setRoutineTask(refreshed);
      onTaskUpdated(taskOptionFromSaved(refreshed));
      setStage('routine');
    } catch (err) {
      console.error('Failed to reopen routine task', err);
      setError('Could not reopen this task. Please try again.');
    } finally {
      setReopening(false);
    }
  };

  if (!request && !error) return null;

  const lockedStatusLabel = (routineTask?.status ?? '').toLowerCase();

  return (
    <>
      {error && stage !== 'routine-locked' && (
        <Modal show onHide={onClose} size="sm">
          <Modal.Header closeButton>
            <Modal.Title>Task</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="danger" className="tw-mb-0">
              {error}
            </Alert>
          </Modal.Body>
        </Modal>
      )}

      {stage === 'loading' && (
        <Modal show onHide={onClose} size="sm">
          <Modal.Body>
            <div className="tw-flex tw-items-center tw-gap-2 tw-py-2">
              <Spinner animation="border" size="sm" aria-hidden="true" />
              <span>Loading task...</span>
            </div>
          </Modal.Body>
        </Modal>
      )}

      <TaskTypeSelector
        visible={stage === 'pick-type'}
        onClose={onClose}
        onSelect={handleSelectType}
        availableTypes={availableTypes}
      />

      {stage === 'routine-locked' && routineTask && (
        <Modal show onHide={onClose} size="sm">
          <Modal.Header closeButton>
            <Modal.Title>Task is {lockedStatusLabel}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && (
              <Alert variant="danger" className="tw-mb-3">
                {error}
              </Alert>
            )}
            <p className="tw-mb-3">
              <strong>{routineTask.taskNumber ?? `Task ${routineTask.id}`}</strong>
              {routineTask.name ? ` — ${routineTask.name}` : ''}
            </p>
            <p className="tw-mb-3 tw-text-sm tw-text-content-muted">
              A {lockedStatusLabel} task cannot be edited. Reopen it to set it back to active,
              or view its details as read-only.
            </p>
            {canReopen && (
              <Form.Group controlId="he-reopen-reason">
                <Form.Label>Reason for reopening *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={reopenReason}
                  onChange={(e) => setReopenReason(e.target.value)}
                  placeholder="Why is this task being reopened?"
                />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" type="button" onClick={() => setStage('routine')}>
              View details
            </Button>
            {canReopen && (
              <Button
                variant="primary"
                type="button"
                disabled={reopening || !reopenReason.trim()}
                onClick={handleReopen}
              >
                {reopening ? 'Reopening...' : 'Reopen task'}
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      )}

      {stage === 'routine' && (
        <RoutineTaskModal
          visible
          onClose={onClose}
          task={routineTask}
          project={project ?? undefined}
          defaultStatus={isCreate ? CREATE_DEFAULT_STATUS : undefined}
          onTaskSaved={finish}
        />
      )}

      {stage === 'quantified' && (
        <QuantifiedTaskModal
          visible
          onClose={onClose}
          task={task}
          taskType="Quantified"
          foremanAreas={foremanAreas}
          project={project ?? undefined}
          initialContractAreas={[]}
          defaultStatus={isCreate ? CREATE_DEFAULT_STATUS : undefined}
          onTaskSaved={finish}
        />
      )}

      {stage === 'generic' && (
        <QPTaskModal
          visible
          onClose={onClose}
          task={task}
          taskType={(task?.type as TaskType) ?? 'Overhead'}
          foremanAreas={foremanAreas}
          project={project ?? undefined}
          initialContractAreas={[]}
          onTaskSaved={finish}
        />
      )}
    </>
  );
};

export default HiredEquipmentTaskModals;
